/**
 * Written pages register themselves. Nobody edits a central list to add one.
 *
 * WHY THIS EXISTS. Adding one e-book page used to mean editing five shared
 * files: an import line and a key in `content/en.ts`, the same two in
 * `content/tr.ts`, a `RouteDef` in `app/routes.ts`, and two keys in
 * `content/pages/links.ts`. Every one of those is an append into the same small
 * region, so two AI-editor runs building two unrelated pages wrote the same
 * lines and the second could not rebase.
 *
 * That was measured on 2026-08-28: after the queue was unpaused, seven agent
 * runs were thrown away against three that landed, and one request reached the
 * three-try cap. The `stories.ts`/`reports.ts` splits did not touch it, because
 * neither of those files is in an e-book page's path.
 *
 * So the lists read the folder instead. A page module exports its page (and,
 * optionally, its route); `en.ts`, `tr.ts` and `routes.ts` discover it. Adding a
 * page is adding one file, and one file cannot collide with another file.
 *
 * TWO THINGS THIS DELIBERATELY DOES NOT DO:
 *
 * 1. It does not key pages by filename. The key is the **export name**, because
 *    `pages/tools.ts` exports four pages and `t.pages[route.id]` is how both
 *    `App.tsx` and `lib/seo.ts` look one up. Filename-keying would have
 *    renamed three routes silently.
 * 2. It does not assume one export per module. Everything a page module exports
 *    is offered, and only the values shaped like a `WrittenPage` are taken —
 *    which is what lets `pages/links.ts` and this module sit in the same folder
 *    as the pages without polluting the map.
 */
import type { WrittenPage } from '@/content/sections'
import type { RouteDef } from '@/app/routes'

/**
 * Is this export a page?
 *
 * A shape check rather than a marker property, because the thirteen page
 * modules that existed when this landed were not going to be edited to add one
 * — and an unmarked page silently missing from the site is exactly the failure
 * this is supposed to prevent. `sections` is the discriminator: every
 * `WrittenPage` has one and nothing else exported from that folder does.
 */
function isWrittenPage(value: unknown): value is WrittenPage {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    Array.isArray(v.sections)
    && typeof v.title === 'string'
    && typeof v.navLabel === 'string'
  )
}

/** Every `WrittenPage` a globbed set of page modules exports, keyed by export name. */
export function pagesFrom(
  modules: Record<string, Record<string, unknown>>,
): Record<string, WrittenPage> {
  const pages: Record<string, WrittenPage> = {}
  // Sorted by module path so a duplicate export name resolves the same way on
  // every machine. Two pages sharing a key is a mistake either way, but a
  // mistake that lands differently in CI than locally is a much worse one.
  for (const path of Object.keys(modules).sort()) {
    for (const [key, value] of Object.entries(modules[path])) {
      if (isWrittenPage(value)) pages[key] = value
    }
  }
  return pages
}

function isRouteDef(value: unknown): value is RouteDef {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return typeof v.id === 'string' && typeof v.path === 'string'
}

/**
 * Every `RouteDef` a globbed set of page modules exports, sorted by path.
 *
 * Sorted rather than in glob order for the same reason as above, and because
 * `routes.ts` feeds `allPages()`, which feeds the sitemap — an unstable order
 * there is a diff in every build.
 */
export function routesFrom(
  modules: Record<string, Record<string, unknown>>,
): RouteDef[] {
  const routes: RouteDef[] = []
  for (const path of Object.keys(modules).sort()) {
    for (const value of Object.values(modules[path])) {
      if (isRouteDef(value)) routes.push(value)
    }
  }
  return routes.sort((a, b) => a.path.localeCompare(b.path))
}
